import React from 'react';

interface SkeletonProps {
    className?: string;
}

/**
 * Base skeleton component with pulse animation
 */
const Skeleton: React.FC<SkeletonProps> = ({ className = '' }) => {
    return (
        <div className={`animate-pulse bg-white/10 rounded ${className}`} />
    );
};

/**
 * Skeleton for text lines
 */
const SkeletonText: React.FC<{ lines?: number; className?: string }> = ({
    lines = 1,
    className = '',
}) => {
    return (
        <div className={`space-y-2 ${className}`}>
            {Array.from({ length: lines }).map((_, i) => (
                <Skeleton
                    key={i}
                    className={`h-4 ${i === lines - 1 ? 'w-3/4' : 'w-full'}`}
                />
            ))}
        </div>
    );
};

/**
 * Skeleton for circular elements (avatars, icons)
 */
const SkeletonCircle: React.FC<{ size?: string; className?: string }> = ({
    size = 'w-12 h-12',
    className = '',
}) => {
    return <Skeleton className={`rounded-full ${size} ${className}`} />;
};

/**
 * Skeleton for cards
 */
const SkeletonCard: React.FC<SkeletonProps> = ({ className = '' }) => {
    return (
        <div className={`p-4 rounded-lg bg-white/5 ${className}`}>
            <Skeleton className="h-32 w-full mb-4 rounded-lg" />
            <SkeletonText lines={2} />
        </div>
    );
};

/**
 * Calendar-specific skeleton
 */
const SkeletonCalendar: React.FC<SkeletonProps> = ({ className = '' }) => {
    return (
        <div className={`flex flex-col items-center justify-center h-full text-white px-4 py-8 ${className}`}>
            {/* Weekday */}
            <Skeleton className="h-5 w-20 mb-2" />

            {/* Large date number */}
            <Skeleton className="h-24 w-32 mb-2 rounded-xl" />

            {/* Month year */}
            <Skeleton className="h-6 w-40 mb-4" />

            {/* Divider */}
            <Skeleton className="h-0.5 w-24 mb-4" />

            {/* Lunar date */}
            <div className="space-y-2 mb-4">
                <Skeleton className="h-5 w-36 mx-auto" />
                <Skeleton className="h-4 w-24 mx-auto" />
            </div>

            {/* Can Chi */}
            <div className="space-y-2 mb-4">
                <Skeleton className="h-4 w-16 mx-auto" />
                <Skeleton className="h-5 w-48 mx-auto" />
            </div>

            {/* Good hours */}
            <div className="flex gap-2 justify-center">
                {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-8 w-16 rounded-full" />
                ))}
            </div>
        </div>
    );
};

/**
 * Note list skeleton
 */
const SkeletonNoteList: React.FC<{ count?: number; className?: string }> = ({
    count = 3,
    className = '',
}) => {
    return (
        <div className={`space-y-3 p-4 ${className}`}>
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                    <SkeletonCircle size="w-8 h-8" />
                    <div className="flex-1">
                        <Skeleton className="h-4 w-3/4 mb-2" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-2/3 mt-1" />
                    </div>
                </div>
            ))}
        </div>
    );
};

export {
    Skeleton,
    SkeletonText,
    SkeletonCircle,
    SkeletonCard,
    SkeletonCalendar,
    SkeletonNoteList,
};
